import React, { lazy, Suspense, useEffect } from 'react';
import { Spinner, Nav } from '@chat/components';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Device, isProd, isTest2, isOt } from '@chat/utils';
import Viewer from 'viewerjs';
import MessageList from '@/components/MessageList';
import HotLabel from '@/components/HotLabel';
import ImHeader from '@/components/ImHeader';
import Footer from '@/components/Footer';
import useFlicker from '@/hooks/useFlicker';
import ToastContainer from '@/components/Toast';
import { dialogMaps } from '@/config/config';
import useObjectState from '@/hooks/useObjectState';
import 'viewerjs/dist/viewer.css';
import Forms from '@/components/Forms';
import QuickEntry from '@/components/QuickEntry';
import HotIssues from '@/components/HotIssues';
import { setDifyLogWorkflowRunId } from '@/redux/index/action';

const { isMobile } = new Device();
// 按需加载移动端适配
if (isMobile) {
    import(/* webpackChunkName: "wap" */ '@/scss/wap/im.scss');
} else {
    import(/* webpackChunkName: "pc" */ '@/scss/pc/im.scss');
}

const Editor = lazy(() => import(/* webpackChunkName: "editor" */ '@/components/Editor'));
const ProAdDifyLogPanel = lazy(
    () => import(/* webpackChunkName: "dify-log-panel" */ '@chat/components/lib/ProAdDifyLogPanel')
);

function IMapp(): JSX.Element {
    let viewer = null;
    const { start, stop } = useFlicker({
        prefix: ['【　　　】', '【新消息】'],
        sound: require(`@/assets/message.mp3`)
    });
    const { show_right_sidebar, is_client } = IM.SDKServer.getState();
    const dispatch = useDispatch();
    const { isLoading, can_faq, base_config, orientation, difyLogWorkflowRunId } = useSelector(
        ({ FaqReducer, IndexReducer }) => ({
            can_faq: FaqReducer.can_faq,
            isLoading: IndexReducer.isLoading,
            base_config: IndexReducer.base_config,
            orientation: IndexReducer.orientation,
            difyLogWorkflowRunId: IndexReducer.dify_log_workflow_run_id
        }),
        shallowEqual
    );
    const { objState: visible, handleObjState: setVisivle } = useObjectState({
        initialValue: {
            end: false, // 结束会话
            closeWebView: false, // 结束会话
            contact: false, // 联系方式
            evaluate: false, // 评价
            notice: false, // 系统公告
            face: false // 表情包
        }
    });
    const show_nav = +base_config?.site.nav_status === 1 && !is_client;
    const handlePopup = ({ popup, open }) => {
        const outline = !window.navigator.onLine;
        if (outline && open) {
            return IM.IMServer.notice('warn', '网络开小差了，请检查网络连接是否正常');
        }
        setVisivle(popup, open);
    };
    const handlePreview = ({ target }) => {
        const el = target instanceof Element ? target : target.parentElement;
        if (!el?.closest('.im-message-block')) {
            return;
        }
        const viewFn = (res, element, url) => {
            if (!res) {
                viewer?.destroy?.();
                viewer = new Viewer(element, {
                    url,
                    toolbar: false,
                    title: false,
                    navbar: false
                });
                viewer.show();
            }
        };
        const hasSrc = el.hasAttribute('data-src');
        let url = 'data-src';
        if (hasSrc) {
            const src = el.getAttribute('data-src');
            if (src === 'src') {
                url = 'src';
            }
            const res = window.IM.SDKServer.openImgViewer([{ src: el.getAttribute(url) }], 0);
            viewFn(res, el, url);
        } else if (el.tagName.toLowerCase() === 'img' && el.getAttribute('src')) {
            const res = window.IM.SDKServer.openImgViewer([{ src: el.getAttribute('src') }], 0);
            viewFn(res, el, 'src');
        }
    };

    // 弹窗
    window.IM.handlePopup = handlePopup;
    // 图片预览
    window.IM.handlePreview = handlePreview;
    // title修改
    window.IM.flickerStart = start;
    window.IM.flickerStop = stop;

    useEffect(() => {
        if (isTest2 || isOt || isProd) {
            IM.InitServer.setup();
        }
        return () => {
            IM.IMServer.removeListener();
        };
    }, []);
    // 横屏模式下设置页面最小高度，在唤起的表情包的情况下可以滚动屏幕
    // useEffect(() => {
    //     // @ts-ignore
    //     document.querySelector('.im-wrapper').style.minHeight =
    //         orientation === 'portrait' ? 'none' : '600px';
    // }, [orientation]);
    return (
        <div
            className={classnames('im-wrapper', {
                'im-wrapper-with-nav': show_nav
            })}
            onClick={() => IM.handlePopup({ popup: 'face', open: false })}
        >
            <ToastContainer limit={1} />
            {dialogMaps.map(
                (item) =>
                    visible[item.name] && (
                        <Suspense fallback={<div />} key={`popup${item.name}`}>
                            <item.Components key={`popup${item.name}`} />
                        </Suspense>
                    )
            )}
            {show_nav && !isMobile && (
                <Nav
                    logo={base_config.site?.nav_logo}
                    user_nick={base_config.user_info?.user_nick}
                    guest={base_config.user_info?.guest}
                />
            )}
            {difyLogWorkflowRunId && !isMobile && (
                <Suspense fallback={<div />}>
                    <ProAdDifyLogPanel
                        mode="floating"
                        open
                        workflowRunId={difyLogWorkflowRunId}
                        request={IM.DifyServer.getWorkflowLog}
                        onClose={() => dispatch(setDifyLogWorkflowRunId(''))}
                    />
                </Suspense>
            )}
            <div className="im-container">
                {show_nav && isMobile && (
                    <Nav
                        logo={base_config.site?.nav_logo}
                        user_nick={base_config.user_info?.user_nick}
                        guest={base_config.user_info?.guest}
                    />
                )}
                <ImHeader />
                <div className={classnames('im-main')}>
                    <div
                        className={classnames('im-left-container', {
                            'has-right-container': !!show_right_sidebar,
                            'not-has-right-container': !show_right_sidebar
                        })}
                        onClick={(e) => handlePreview(e)}
                    >
                        {/* 列表loading */}
                        {isLoading && <Spinner type="list" />}
                        {/* 表单入口 */}
                        {!_.isEmpty(base_config.form_setting) && <Forms />}
                        {/* 消息列表 */}
                        <MessageList />
                        <div className="im-chat-wrapper">
                            {can_faq && <HotLabel />}
                            <Suspense fallback={<div />}>
                                <Editor visible={visible} />
                            </Suspense>
                        </div>
                    </div>
                    {show_right_sidebar && (
                        <div className="im-right-container">
                            {/* 快捷入口 */}
                            <QuickEntry />
                            {/* 热门问题 */}
                            <HotIssues />
                        </div>
                    )}
                </div>
            </div>
            {/* 底部 */}
            <Footer />
        </div>
    );
}

export default IMapp;
